// frontend/src/pages/BlogIndexPage/BlogIndexPage.jsx

import React, { useState, useCallback, useContext, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Paper, Grid, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import './BlogPages.css'; // Use the new shared stylesheet
import { AuthContext } from '../../contexts/AuthContext';

const gridContainerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
const gridItemVariants = { hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } };

const BlogIndexPage = () => {
    const { t } = useTranslation();
    const { fetchWithAuth } = useContext(AuthContext);
    const [isScrolled, setIsScrolled] = useState(false);
    const containerRef = useRef(null);

    const handleScroll = (e) => {
        if (e.target.scrollTop > 20) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);

    const particlesConfig = {
        background: { color: { value: 'transparent' } },
        fpsLimit: 120,
        interactivity: { events: { onHover: { enable: true, mode: 'repulse' }, resize: true }, modes: { repulse: { distance: 100, duration: 0.4 } } },
        particles: {
            color: { value: '#ffffff' },
            links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.4, width: 1 },
            move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 1, straight: false },
            number: { density: { enable: true, area: 1000 }, value: 50 }, // Less dense for text-heavy pages
            opacity: { value: 0.5 },
            shape: { type: 'circle' },
            size: { value: { min: 1, max: 3 } },
        },
        detectRetina: true,
    };

    // TanStack Query with 30-minute caching
    const { 
        data: posts = [], 
        isLoading, 
        error: queryError 
    } = useQuery({
        queryKey: ['blogPosts'],
        queryFn: async () => {
            const response = await fetchWithAuth('/api/blog/posts');
            if (!response.ok) {
                throw new Error('Failed to fetch blog posts.');
            }
            return response.json();
        },
        staleTime: 1000 * 60 * 30, // 30 minutes
    });

    const error = queryError?.message || null;

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    return (
        <div 
            ref={containerRef}
            onScroll={handleScroll}
            className={`blog-page-container ${isScrolled ? 'scrolled' : ''}`}
        >
            <Particles id="tsparticles-blog" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('blog.helmet.indexTitle')}</title>
                <meta name="description" content={t('blog.helmet.indexDescription')} />
            </Helmet>
            
            <div className="blog-nav-container">
                <UniversalNav />
            </div>

            <main className="blog-content-wrapper">
                <Box className="blog-glass-container">
                    <Box className="blog-header" component="div">
                        <Typography variant="h3" component="h1">{t('blog.index.mainTitle')}</Typography>
                        <Typography variant="subtitle1" component="p">{t('blog.index.subtitle')}</Typography>
                    </Box>
                    
                    {isLoading && (
                        <LoadingIndicator 
                            messages={{
                                general: { key: 'blog.index.loading' }
                            }} 
                        />
                    )}
                    {error && <ErrorMessage message={error} />}
                    
                    {!isLoading && !error && (
                        // THE FIX: Increased spacing from 4 to 5 for more vertical distance
                        <Grid container spacing={5} component={motion.div} variants={gridContainerVariants} initial="hidden" animate="visible" className="blog-grid">
                            {posts.map((post) => (
                                <Grid item xs={12} md={6} key={post.slug} component={motion.div} variants={gridItemVariants}>
                                    <Paper className="blog-post-card">
                                        <Typography variant="h5" component="h2" className="card-title">{post.title}</Typography>
                                        <Typography variant="body2" className="card-metadata">
                                            {t('blog.by')} {post.author} {t('blog.on')} {formatDate(post.date)}
                                        </Typography>
                                        <Typography variant="body1" className="card-summary">{post.summary}</Typography>
                                        <Button
                                            component={RouterLink}
                                            to={`/blog/${post.slug}`}
                                            variant="outlined"
                                            endIcon={<ArrowForwardIcon />}
                                            className="read-more-btn"
                                        >
                                            {t('blog.index.readMore')}
                                        </Button>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </main>
        </div>
    );
};

export default BlogIndexPage;